import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Home from "./pages/Home.tsx";
import { AppShell } from "@/components/layout/AppShell";
import { LanguageProvider } from "@/i18n/LanguageProvider";
import { ConsentBanner } from "@/components/ConsentBanner";

const FlightDetail = lazy(() => import("./pages/FlightDetail.tsx"));
const RouteResults = lazy(() => import("./pages/RouteResults.tsx"));
const AirportDetail = lazy(() => import("./pages/AirportDetail.tsx"));
const Reseservice = lazy(() => import("./pages/Reseservice.tsx"));
const Routle = lazy(() => import("./pages/Routle.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const FlygradarSverige = lazy(() => import("./pages/FlygradarSverige.tsx"));
const FlygradarEuropa = lazy(() => import("./pages/FlygradarEuropa.tsx"));
const LiveFlights = lazy(() => import("./pages/LiveFlights.tsx"));
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const CookiesPage = lazy(() => import("./pages/Cookies.tsx"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <LanguageProvider>
          <Suspense fallback={null}>
            <Routes>
              <Route element={<AppShell />}>
                {/* Swedish (default) routes */}
                <Route path="/" element={<Home />} />
                <Route path="/flight/:id" element={<FlightDetail />} />
                <Route path="/route/:pair" element={<RouteResults />} />
                <Route path="/airport/:code" element={<AirportDetail />} />
                <Route path="/reseservice" element={<Reseservice />} />
                <Route path="/spel" element={<Routle />} />
                <Route path="/spel/debug" element={<Routle debug />} />
                <Route path="/daily" element={<Routle />} />
                <Route path="/flygradar/sverige" element={<FlygradarSverige />} />
                <Route path="/flygradar/europa" element={<FlygradarEuropa />} />
                <Route path="/live" element={<LiveFlights />} />
                <Route path="/integritetspolicy" element={<Privacy />} />
                <Route path="/villkor" element={<Terms />} />
                <Route path="/cookies" element={<CookiesPage />} />

                {/* English routes (/en/...) */}
                <Route path="/en" element={<Home />} />
                <Route path="/en/flight/:id" element={<FlightDetail />} />
                <Route path="/en/route/:pair" element={<RouteResults />} />
                <Route path="/en/airport/:code" element={<AirportDetail />} />
                <Route path="/en/travel-services" element={<Reseservice />} />
                <Route path="/en/game" element={<Routle />} />
                <Route path="/en/game/debug" element={<Routle debug />} />
                <Route path="/en/flight-radar/sweden" element={<FlygradarSverige />} />
                <Route path="/en/flight-radar/europe" element={<FlygradarEuropa />} />
                <Route path="/en/live" element={<LiveFlights />} />
                <Route path="/en/privacy" element={<Privacy />} />
                <Route path="/en/terms" element={<Terms />} />
                <Route path="/en/cookies" element={<CookiesPage />} />

                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Suspense>
          <ConsentBanner />
        </LanguageProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
